import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

// ── Composição ───────────────────────────────────────────────────────────────
export interface ComposicaoRow { id: number; materiaPrimaId: number; materiaPrimaDescricao: string; quantidadeProducao: number; unidadeId: number; }
export interface SalvarComposicaoRequest { materiaPrimaId: number; quantidadeProducao: number; unidadeId: number; }

export const useProdutoComposicao = (produtoId: string | undefined) => {
  const qc = useQueryClient(); const numId = produtoId ? Number(produtoId) : undefined;
  const key = ["produto-composicao", numId]; const base = `/api/produtos/${numId}/composicao`;
  const query = useQuery({ queryKey: key, queryFn: async () => (await apiClient.get<ComposicaoRow[]>(base)).data, enabled: !!numId });
  const create = useMutation({ mutationFn: async (b: SalvarComposicaoRequest) => (await apiClient.post(base, b)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Item de composição adicionado!"); }, onError: (e: Error) => toast.error(`Erro: ${e.message}`) });
  const remove = useMutation({ mutationFn: async (id: number) => { await apiClient.delete(`${base}/${id}`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Item removido!"); }, onError: (e: Error) => toast.error(`Erro: ${e.message}`) });
  return { itens: query.data ?? [], create: create.mutate, remove: remove.mutate };
};

// ── Adicionais ───────────────────────────────────────────────────────────────
export interface AdicionalRow { id: number; adicionalId: number; adicionalDescricao: string; quantidadeMaxima: number; }
export interface SalvarAdicionalRequest { adicionalId: number; quantidadeMaxima: number; }

export const useProdutoAdicionais = (produtoId: string | undefined) => {
  const qc = useQueryClient(); const numId = produtoId ? Number(produtoId) : undefined;
  const key = ["produto-adicionais", numId]; const base = `/api/produtos/${numId}/adicionais`;
  const query = useQuery({ queryKey: key, queryFn: async () => (await apiClient.get<AdicionalRow[]>(base)).data, enabled: !!numId });
  const create = useMutation({ mutationFn: async (b: SalvarAdicionalRequest) => (await apiClient.post(base, b)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Adicional vinculado!"); }, onError: (e: Error) => toast.error(`Erro: ${e.message}`) });
  const remove = useMutation({ mutationFn: async (id: number) => { await apiClient.delete(`${base}/${id}`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Adicional removido!"); }, onError: (e: Error) => toast.error(`Erro: ${e.message}`) });
  return { itens: query.data ?? [], create: create.mutate, remove: remove.mutate };
};

// ── Opcionais ────────────────────────────────────────────────────────────────
export interface OpcionalRow { id: number; opcionalId: number; opcionalDescricao: string; quantidadeMaxima: number; }

export const useOpcionaisCombo = () => {
  const { data = [] } = useQuery({
    queryKey: ["opcionais-combo"],
    queryFn: async () => (await apiClient.get<{ id: number; descricao?: string | null }[]>("/api/opcionais")).data,
    staleTime: 5 * 60 * 1000,
  });
  return data;
};

export const useProdutoOpcionais = (produtoId: string | undefined) => {
  const qc = useQueryClient(); const numId = produtoId ? Number(produtoId) : undefined;
  const key = ["produto-opcionais", numId]; const base = `/api/produtos/${numId}/opcionais`;
  const query = useQuery({ queryKey: key, queryFn: async () => (await apiClient.get<OpcionalRow[]>(base)).data, enabled: !!numId });
  const create = useMutation({ mutationFn: async (opcionalId: number) => (await apiClient.post(base, { opcionalId })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Opcional vinculado!"); }, onError: (e: Error) => toast.error(`Erro: ${e.message}`) });
  const remove = useMutation({ mutationFn: async (id: number) => { await apiClient.delete(`${base}/${id}`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); toast.success("Opcional removido!"); }, onError: (e: Error) => toast.error(`Erro: ${e.message}`) });
  return { itens: query.data ?? [], create: create.mutate, remove: remove.mutate };
};
